import { FederationContext, OrganisationContext } from '@/context';
import { usePermission } from '@/hooks';
import permissions from '@hogwarts/permissions';
import { REGION_DISPLAY_LABELS } from '@hogwarts/plugin-cognita/lib/jobs/report_utils';
import { useForms } from '@hogwarts/ui-components-forms';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';


export const useEditReportForm = ({ onUpdate, onDelete, report }) => {
  const { t } = useTranslation();
  const organisation = useContext(OrganisationContext);
  const federation = useContext(FederationContext);
  const canAddOrganisationReports = usePermission(
    permissions.REPORT_CREATE,
    organisation?.id
  );
  const canAddFederationReports = usePermission(
    permissions.REPORT_CREATE,
    federation?.id
  );
  const getOwnerOptions = () => {
    const options = [
      { id: 'USER', value: t('Only Me') },
      {
        id: 'ORGANISATION',
        value: organisation.name,
        disabled: !canAddOrganisationReports,
      },
    ];
    if (federation) {
      options.push({
        id: 'FEDERATION',
        value: federation.name,
        disabled: !canAddFederationReports,
      });
    }

    // Add region tag options for sharing (only for Cognita federation)
    if (federation?.key === 'cognita') {
      Object.entries(REGION_DISPLAY_LABELS).forEach(([tagKey, displayLabel]) => {
        options.push({
          id: `TAG:${tagKey.toLowerCase()}`,
          value: `${t('Region')}: ${displayLabel}`,
          disabled: !canAddOrganisationReports, // Use same permission as organisation reports
        });
      });
    }

    return options;
  };

  // Determine the correct ownerType for the form based on existing report data
  const getInitialOwnerType = () => {
    if (!report) return 'USER';

    const reportOwnerType = report.ownerType || report.owner?.type;
    const reportOwnerId = report.ownerId || report.owner?.id;

    // Check if this is a tag-based report
    // A tag-based report is owned by the federation and has organisationTags
    if (
      reportOwnerType === 'FEDERATION' &&
      reportOwnerId === federation?.id &&
      report.options?.organisationTags?.length > 0
    ) {
      const tagKey = report.options.organisationTags[0]; // Use first tag
      const initialOwnerType = `TAG:${tagKey.toLowerCase()}`;
      return initialOwnerType;
    }

    const finalOwnerType = reportOwnerType || 'USER';
    return finalOwnerType;
  };

  return useForms({
    title: t('Edit Report'),
    allowCancel: false,
    initialValues: (() => {
      const initialOwnerType = getInitialOwnerType();
      return {
        ownerType: initialOwnerType,
        name: report?.name || '',
        description: report?.description || '',
        options: report?.options || {},
      };
    })(),
    fields: [
      {
        key: 'name',
        type: 'textbox',
        label: t('Name'),
        validate: 'required',
        maxLength: 40,
      },
      {
        key: 'description',
        type: 'textarea',
        label: t('Description'),
        maxLength: 120,
      },

      {
        key: 'ownerType',
        type: 'singleselect',
        label: t('Shared with'),
        values: getOwnerOptions(),
        validate: 'required',
        help: {
          type: 'info',
          description: t(
            'Reports can be visible to users, organisations, federations, or regions. Sharing your report will make it available for others to view. Region sharing makes reports visible to all organisations with matching tags.'
          ),
        },
      },

      {
        type: 'separator',
      },
      ...((federation?.hasSharedTeam &&
        federation?.canReadShared && [
          {
            type: 'title',
            label: t('Federation'),
          },
          {
            key: 'options.includeShared',
            type: 'checkbox',
            label: t('Include Central Team Profiles'),
            maxLength: 120,
            large: false,
          },
          {
            type: 'separator',
          },
        ]) ||
        []),
      {
        type: 'title',
        label: t('Excel Export'),
      },
      {
        key: 'options.excel.notes',
        large: false,
        type: 'checkbox',
        label: t('Include Errors and Warnings as Notes'),
      },
      {
        key: 'options.excel.singleTab',
        large: false,
        type: 'checkbox',
        label: t('Export All Profiles onto a Single Tab'),
      },
      {
        key: 'options.excel.includeSummary',
        large: false,
        type: 'checkbox',
        label: t('Include Summary Page'),
      },
      {
        key: 'options.excel.includeHeaders',
        large: false,
        type: 'checkbox',
        label: t('Include Section Header Row'),
      },
    ],
    successToastMessage: t('Report Updated'),
    onSave: async (values) => {
      let ownerId;
      let backendOwnerType;
      let updatedOptions = { ...values.options };

      if (values.ownerType.startsWith('TAG:')) {
        // Handle region tag sharing
        const tagKey = values.ownerType.replace('TAG:', '');
        backendOwnerType = 'FEDERATION'; // Use federation as the owner for region-shared reports
        ownerId = federation.id; // Use federation as owner
        updatedOptions.organisationTags = [tagKey]; // Set the tag for filtering
      } else {
        // Not a tag-based share, clear any existing organisationTags
        backendOwnerType = values.ownerType;
        // Explicitly remove organisationTags
        updatedOptions.organisationTags = undefined;

        switch (values.ownerType) {
          case 'ORGANISATION': {
            ownerId = organisation.id;
            break;
          }
          case 'FEDERATION': {
            ownerId = federation.id;
            break;
          }
          default: {
            break;
          }
        }
      }

      // analytics.track(analytics.trackingKeys.reports.REPORT_UPDATED);
      await onUpdate({
        ...values,
        ownerType: backendOwnerType,
        ownerId,
        options: updatedOptions,
      });
    },
    allowDelete: !!onDelete,
    onDelete: async () => {
      return onDelete();
    },
    // deleteText: section.deleted ? 'Restore' : 'Delete',
  });
};
